import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/shared/api';
import {
  ShieldCheck,
  Download,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  X,
} from 'lucide-react';

interface AuditEvent {
  id: string;
  occurred_at: string;
  event_type: string;
  severity: string;
  user_email: string | null;
  user_role: string | null;
  user_satker: string | null;
  ip_address: string | null;
  resource_type: string | null;
  resource_id: string | null;
  action: string | null;
  result: string;
  record_hash: string;
  prev_hash: string | null;
  metadata: any;
}

interface Stats {
  total: number;
  today: number;
  by_event_type: { event_type: string; count: number }[];
  by_result: { result: string; count: number }[];
  by_user: { user_email: string; count: number }[];
}

interface ChainVerify {
  checked_count: number;
  valid: boolean;
  broken_at?: string;
}

export default function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventType, setEventType] = useState<string>(searchParams.get('event_type') || '');
  const [userEmail, setUserEmail] = useState('');
  const [resultFilter, setResultFilter] = useState<string>(searchParams.get('result') || '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  // Sync URL → state when drawer click changes URL
  useEffect(() => {
    setEventType(searchParams.get('event_type') || '');
    setResultFilter(searchParams.get('result') || '');
    setPage(0);
  }, [searchParams]);

  // Update URL when filter changes via UI
  const updateUrl = (et: string, rf: string) => {
    const p: Record<string, string> = {};
    if (et) p.event_type = et;
    if (rf) p.result = rf;
    setSearchParams(p, { replace: true });
  };

  const limit = 100;

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (eventType) p.set('event_type', eventType);
    if (userEmail) p.set('user_email', userEmail);
    if (resultFilter) p.set('result', resultFilter);
    if (fromDate) p.set('from_date', new Date(fromDate).toISOString());
    if (toDate) p.set('to_date', new Date(toDate).toISOString());
    p.set('limit', String(limit));
    p.set('offset', String(page * limit));
    return p.toString();
  };

  const { data: events, isLoading, refetch } = useQuery<AuditEvent[]>({
    queryKey: ['audit-events', eventType, userEmail, resultFilter, fromDate, toDate, page],
    queryFn: () => api(`/api/v1/audit/events?${buildQuery()}`),
  });

  const { data: stats } = useQuery<Stats>({
    queryKey: ['audit-stats'],
    queryFn: () => api('/api/v1/audit/stats'),
  });

  const { data: types } = useQuery<string[]>({
    queryKey: ['audit-event-types'],
    queryFn: () => api('/api/v1/audit/event-types'),
  });

  const verifyMut = useMutation({
    mutationFn: async () => {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/audit/verify-chain?limit=5000`,
        { headers: { 'X-Dev-User': 'admin@netra.local' } },
      );
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json() as Promise<ChainVerify>;
    },
  });

  const exportCSV = async () => {
    const p = new URLSearchParams();
    if (eventType) p.set('event_type', eventType);
    if (fromDate) p.set('from_date', new Date(fromDate).toISOString());
    if (toDate) p.set('to_date', new Date(toDate).toISOString());
    const resp = await fetch(
      `${import.meta.env.VITE_API_URL}/api/v1/audit/events.csv?${p.toString()}`,
      { headers: { 'X-Dev-User': 'admin@netra.local' } },
    );
    if (!resp.ok) {
      alert('Gagal export: ' + (await resp.text()));
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setEventType('');
    setUserEmail('');
    setResultFilter('');
    setFromDate('');
    setToDate('');
    setPage(0);
    setSearchParams({}, { replace: true });
  };

  return (
    <div>
      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <ShieldCheck className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">Audit Trail</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— Hash Chain · Forensik</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">UU ITE Pasal 5</span>
      </div>

      {/* KPI STRIP */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-b border-ink-line mb-6">
          <Kpi label="Total Events" value={stats.total} />
          <Kpi label="Hari Ini" value={stats.today} />
          <Kpi
            label="OK Results"
            value={stats.by_result.find((r) => r.result === 'OK')?.count || 0}
          />
          <Kpi
            label="Hash Chain"
            value={verifyMut.data?.valid === undefined ? '—' : verifyMut.data.valid ? '✓ Valid' : '✗ Broken'}
            valueColor={verifyMut.data?.valid === false ? 'text-crimson' : 'text-olive'}
            foot={verifyMut.data ? `${verifyMut.data.checked_count} block diperiksa` : 'Belum diverifikasi'}
            last
          />
        </div>
      )}

      {/* TOOLBAR */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <select
          value={eventType}
          onChange={(e) => { setEventType(e.target.value); setPage(0); updateUrl(e.target.value, resultFilter); }}
          className="px-3 h-9 border border-ink-line font-mono text-[11px] bg-paper text-ink focus:border-brass outline-none"
        >
          <option value="">Semua Event Type</option>
          {(types || []).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <input
          type="text"
          value={userEmail}
          onChange={(e) => { setUserEmail(e.target.value); setPage(0); }}
          placeholder="Filter email..."
          className="px-3 h-9 border border-ink-line font-mono text-[11px] bg-paper text-ink focus:border-brass outline-none w-48"
        />

        <select
          value={resultFilter}
          onChange={(e) => { setResultFilter(e.target.value); setPage(0); updateUrl(eventType, e.target.value); }}
          className="px-3 h-9 border border-ink-line font-mono text-[11px] bg-paper text-ink focus:border-brass outline-none"
        >
          <option value="">Semua Result</option>
          <option value="OK">OK</option>
          <option value="DENIED">DENIED</option>
          <option value="ERROR">ERROR</option>
        </select>

        <input
          type="date"
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
          className="px-3 h-9 border border-ink-line font-mono text-[11px] bg-paper text-ink focus:border-brass outline-none"
        />
        <span className="font-mono text-[10px] text-ink-3">—</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(0); }}
          className="px-3 h-9 border border-ink-line font-mono text-[11px] bg-paper text-ink focus:border-brass outline-none"
        />

        <button
          onClick={resetFilters}
          className="px-3 h-9 border border-ink-line font-mono text-[10px] uppercase tracking-wider text-ink-2 hover:border-ink hover:text-ink transition"
        >
          Reset
        </button>

        <div className="ml-auto flex gap-2">
          <button
            onClick={() => verifyMut.mutate()}
            disabled={verifyMut.isPending}
            className="px-3 h-9 border border-bluestamp text-bluestamp hover:bg-bluestamp hover:text-paper transition font-mono text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            {verifyMut.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
            Verifikasi Chain
          </button>
          <button
            onClick={exportCSV}
            className="px-3 h-9 border border-brass text-brass hover:bg-brass hover:text-paper transition font-mono text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
          >
            <Download className="w-3 h-3" />
            Export CSV
          </button>
          <button
            onClick={() => refetch()}
            className="px-3 h-9 border border-ink-line text-ink-2 hover:border-ink hover:text-ink transition font-mono text-[10px] uppercase tracking-wider"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* CHAIN VERIFY RESULT BANNER */}
      {verifyMut.data && (
        <div
          className={`p-3 mb-4 border-2 font-mono text-xs flex items-start gap-2 ${
            verifyMut.data.valid
              ? 'border-olive bg-olive/5 text-olive'
              : 'border-crimson bg-crimson/5 text-crimson'
          }`}
        >
          {verifyMut.data.valid ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          <div>
            <strong>Hash chain {verifyMut.data.valid ? 'VALID' : 'RUSAK'}.</strong>{' '}
            {verifyMut.data.checked_count} block diperiksa.{' '}
            {verifyMut.data.broken_at && (
              <>Break point: <code className="text-[10px]">{verifyMut.data.broken_at}</code></>
            )}
          </div>
        </div>
      )}

      {/* EVENT TABLE */}
      <div className="border border-ink-line bg-paper-2/30 overflow-x-auto">
        <div className="grid grid-cols-[160px_180px_220px_140px_140px_120px_80px] min-w-[1080px] gap-2 px-3 py-2 border-b border-ink bg-paper-2/60 font-mono text-[9px] uppercase tracking-widest text-ink-3">
          <span>Waktu</span>
          <span>Event Type</span>
          <span>User</span>
          <span>Role · Satker</span>
          <span>Resource · Action</span>
          <span>Hash</span>
          <span>Result</span>
        </div>

        {isLoading && (
          <p className="px-4 py-8 font-mono text-xs text-ink-3 text-center">Memuat audit...</p>
        )}

        {events && events.length === 0 && !isLoading && (
          <p className="px-4 py-8 font-mono text-xs text-ink-3 text-center italic">
            Tidak ada event sesuai filter.
          </p>
        )}

        {events && events.map((e) => (
          <button
            key={e.id}
            onClick={() => setSelected(e)}
            className="w-full text-left grid grid-cols-[160px_180px_220px_140px_140px_120px_80px] min-w-[1080px] gap-2 px-3 py-2 border-b border-dashed border-ink-line/60 last:border-b-0 font-mono text-[10px] text-ink-2 hover:bg-brass-soft/30 transition"
          >
            <span className="text-ink-3">
              {new Date(e.occurred_at).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'medium' })}
            </span>
            <span className="font-bold text-ink truncate">{e.event_type}</span>
            <span className="truncate">{e.user_email || '—'}</span>
            <span className="text-bluestamp truncate">{e.user_role || '—'} · {e.user_satker || '—'}</span>
            <span className="truncate">
              {e.resource_type || '—'}
              {e.action ? ` · ${e.action}` : ''}
            </span>
            <span className="truncate text-brass" title={e.record_hash}>
              {e.record_hash.slice(0, 12)}…
            </span>
            <span
              className={
                e.result === 'OK' ? 'text-olive font-bold' :
                e.result === 'DENIED' ? 'text-maroon font-bold' :
                'text-crimson font-bold'
              }
            >
              {e.result}
            </span>
          </button>
        ))}
      </div>

      {/* PAGINATION */}
      <div className="mt-3 flex items-center gap-3 font-mono text-[11px]">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="px-3 py-1.5 border border-ink-line text-ink-2 hover:border-ink disabled:opacity-30"
        >
          ← Prev
        </button>
        <span className="text-ink-3">Page {page + 1} · {limit} rows/page</span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={!events || events.length < limit}
          className="px-3 py-1.5 border border-ink-line text-ink-2 hover:border-ink disabled:opacity-30"
        >
          Next →
        </button>
      </div>

      {/* DETAIL MODAL */}
      {selected && (
        <div
          className="fixed inset-0 bg-ink/60 backdrop-blur z-[100] grid place-items-center p-8"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-paper border-2 border-ink max-w-3xl w-full max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-ink bg-paper-2/40">
              <ShieldCheck className="w-4 h-4 text-brass" />
              <h3 className="font-display font-bold text-[16px]">Audit Event Detail</h3>
              <button
                onClick={() => setSelected(null)}
                className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 font-mono text-[11px]">
              <Field label="Event ID" value={selected.id} />
              <Field label="Occurred At" value={new Date(selected.occurred_at).toLocaleString('id-ID')} />
              <Field label="Event Type" value={selected.event_type} bold />
              <Field label="Severity" value={selected.severity} />
              <Field label="Result" value={selected.result} bold colorClass={selected.result === 'OK' ? 'text-olive' : 'text-crimson'} />
              <Field label="User Email" value={selected.user_email || '—'} />
              <Field label="User Role" value={selected.user_role || '—'} />
              <Field label="User Satker" value={selected.user_satker || '—'} />
              <Field label="IP Address" value={selected.ip_address || '—'} />
              <Field label="Resource" value={`${selected.resource_type || '—'} ${selected.resource_id ? `· ${selected.resource_id}` : ''}`} />
              <Field label="Action" value={selected.action || '—'} />
              <div className="col-span-2 pt-3 border-t border-dashed border-ink-line">
                <div className="label-mono mb-1">Record Hash</div>
                <code className="block text-[10px] text-brass break-all">{selected.record_hash}</code>
              </div>
              <div className="col-span-2">
                <div className="label-mono mb-1">Prev Hash</div>
                <code className="block text-[10px] text-ink-3 break-all">{selected.prev_hash || '(genesis)'}</code>
              </div>
              {selected.metadata && (
                <div className="col-span-2 pt-3 border-t border-dashed border-ink-line">
                  <div className="label-mono mb-1">Metadata</div>
                  <pre className="bg-paper-2/40 p-3 text-[10px] text-ink-2 overflow-x-auto">
                    {JSON.stringify(selected.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TOP STATS GRID */}
      {stats && (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <StatList title="Top Event Types" items={stats.by_event_type.slice(0, 8)} accessor={(x) => ({ label: x.event_type, count: x.count })} />
          <StatList title="By Result" items={stats.by_result} accessor={(x) => ({ label: x.result, count: x.count })} />
          <StatList title="Top Users" items={stats.by_user.slice(0, 8)} accessor={(x) => ({ label: x.user_email, count: x.count })} />
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, foot, last, valueColor }: { label: string; value: number | string; foot?: string; last?: boolean; valueColor?: string }) {
  return (
    <div className={`px-5 py-4 flex flex-col gap-2 ${last ? '' : 'border-r border-ink-line'}`}>
      <div className="label-mono">{label}</div>
      <div className={`font-display font-bold text-[32px] leading-none tracking-tighter tabular-nums ${valueColor || ''}`}>
        {typeof value === 'number' ? value.toLocaleString('id-ID') : value}
      </div>
      {foot && (
        <div className="pt-2 border-t border-dashed border-ink-line font-mono text-[9px] tracking-wider text-ink-3 uppercase">
          {foot}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, bold, colorClass }: { label: string; value: string; bold?: boolean; colorClass?: string }) {
  return (
    <div>
      <div className="label-mono mb-0.5">{label}</div>
      <div className={`${bold ? 'font-bold' : ''} ${colorClass || 'text-ink'}`}>{value}</div>
    </div>
  );
}

function StatList<T>({
  title,
  items,
  accessor,
}: {
  title: string;
  items: T[];
  accessor: (x: T) => { label: string; count: number };
}) {
  const max = Math.max(1, ...items.map((i) => accessor(i).count));
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ink-line">
        <h3 className="font-display font-bold text-[14px] tracking-tight">{title}</h3>
      </div>
      <div className="space-y-1.5 font-mono text-[11px]">
        {items.map((it, i) => {
          const a = accessor(it);
          const pct = (a.count / max) * 100;
          return (
            <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-center">
              <div className="min-w-0">
                <div className="truncate text-ink-2">{a.label || '(none)'}</div>
                <div className="h-1.5 bg-paper-2/40 mt-1">
                  <div className="h-full bg-brass" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className="text-right font-bold text-ink tabular-nums">{a.count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
